'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  side?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
}

const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  ({ open, onClose, title, description, side = 'right', children, className }, ref) => (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-modal-backdrop bg-black/40 backdrop-blur-sm data-[state=open]:animate-enter-fade data-[state=closed]:animate-enter-fade"
        />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed top-0 z-modal h-full w-80 max-w-[85vw] bg-card shadow-soft-xl',
            'flex flex-col border-border',
            'data-[state=open]:animate-enter-slide-x data-[state=closed]:animate-exit-slide-x',
            side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
            className
          )}
        >
          {title && (
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <DialogPrimitive.Title className="text-lg font-semibold">
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:shadow-glow-focus">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
          )}
          {description && (
            <DialogPrimitive.Description className="px-6 py-2 text-sm text-muted-foreground">
              {description}
            </DialogPrimitive.Description>
          )}
          <div className="flex-1 overflow-y-auto p-6">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
);
Drawer.displayName = 'Drawer';

export { Drawer };
